import fetchEndpoint from '@/services/fetchEndpoint';
import buildResultEndpoint from '@/utils/buildResultEndpoint';
import {
  buildCategoryFromAnnotatedSample,
  computeEvidenceDistributionMetrics,
  metricsFromAnnotatedFieldSamples,
  type CategoryDistribution,
  type EvidenceDistributionMetrics,
} from '@/utils/evidenceDistributionMetrics';
import { EVIDENCE_SAMPLE_SIZE } from '@/utils/evidenceSampleSize';
import { getDefaultEvidenceFiltersForMapping } from '@/utils/mappingDefaultEvidenceFilters';

export type EvidenceFieldFilterName =
  | 'ancestry_name'
  | 'disease_model_in_doc'
  | 'sex_in_doc'
  | 'associationType'
  | 'chemsInSentence';

export type EvidenceDistributionScope = {
  view: string;
  source: string;
  tab: string;
  searchItems?: Array<{ id?: string; chemID?: string; chemPrefName?: string }>;
  isMapping?: boolean;
  mappingContent?: Record<string, unknown> | null;
};

type FetchAuth = {
  authorization: unknown;
  setAuthError?: (error: unknown) => void;
  isAuthenticated?: boolean;
};

type CountResponse = {
  status?: string | number;
  message?: string;
  error?: string;
  payload?: unknown;
  data?: unknown;
  paging?: { totalElements?: number };
};

type AnnotatedSample = {
  rows: Array<Record<string, unknown>>;
  annotatedCount: number;
};

const CACHE_TTL_MS = 15 * 60 * 1000;
const metricsCache = new Map<string, { metrics: EvidenceDistributionMetrics; expiresAt: number }>();
const inflightByKey = new Map<string, Promise<EvidenceDistributionMetrics>>();

function mappingDefaultFilters(scope: EvidenceDistributionScope) {
  if (!scope.isMapping || !scope.mappingContent) return [];
  return (
    getDefaultEvidenceFiltersForMapping(
      scope.mappingContent as {
        name: string;
        selectedPmYear?: number;
        selectedPmYearFrom?: number;
        selectedPmYearTo?: number;
        selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL';
        selectedPolarity?: 'Positive' | 'Negative';
      },
    ) ?? []
  );
}

function buildEvidenceUrl(
  scope: EvidenceDistributionScope,
  pageSize: number,
  defaultFilterValue: Array<Record<string, unknown>>,
): string | null {
  const built = buildResultEndpoint({
    view: scope.view,
    source: scope.source,
    tab: scope.tab,
    searchItems: scope.searchItems ?? [],
    isEvidence: true,
    isMapping: Boolean(scope.isMapping),
    mappingContent: (scope.mappingContent as any) ?? null,
    pageNum: 0,
    pageSize,
    orderByParam: '',
    order: '',
    defaultFilterValue: [...mappingDefaultFilters(scope), ...defaultFilterValue] as any,
    sourceWeights: [],
    ignoreUrlFilters: true,
  });
  return built?.url ?? null;
}

function withAuthQuery(url: string, isAuthenticated?: boolean): string {
  if (isAuthenticated) return url;
  return url.includes('skey=') ? url : `${url}${url.includes('?') ? '&' : '?'}skey=unauth`;
}

function readRows(response: CountResponse | null | undefined): Array<Record<string, unknown>> {
  if (Array.isArray(response?.payload)) return response.payload as Array<Record<string, unknown>>;
  if (Array.isArray(response?.data)) return response.data as Array<Record<string, unknown>>;
  return [];
}

function readTotal(response: CountResponse | null | undefined): number {
  const rows = readRows(response);
  const total = Number(response?.paging?.totalElements);
  if (Number.isFinite(total) && total >= 0) return total;
  return rows.length;
}

function assertOk(response: CountResponse | null | undefined, context: string): void {
  if (!response) throw new Error(`${context}: empty response`);
  if (response.status === 'OK' || response.status === 200) return;
  const detail = response.message || response.error || String(response.status);
  throw new Error(`${context}: ${detail}`);
}

async function fetchEvidencePage(url: string, auth: FetchAuth): Promise<CountResponse> {
  const finalUrl = withAuthQuery(url, auth.isAuthenticated);
  return (await fetchEndpoint(finalUrl, auth.authorization, auth.setAuthError)) as CountResponse;
}

function scopeCacheKey(scope: EvidenceDistributionScope): string {
  const mapping = scope.mappingContent as {
    mappingItems?: Array<{ filterParam?: string; filterValue?: unknown }>;
    selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL';
    selectedPolarity?: 'Positive' | 'Negative';
  } | null;
  const mappingItems = mapping?.mappingItems ?? [];
  return JSON.stringify({
    view: scope.view,
    source: scope.source,
    tab: String(scope.tab ?? '').toUpperCase(),
    isMapping: Boolean(scope.isMapping),
    searchItems: (scope.searchItems ?? []).map(item => item.id ?? item.chemID ?? ''),
    mapping: mappingItems.map(item => [item.filterParam, item.filterValue]),
    selectedReferenceType: mapping?.selectedReferenceType ?? null,
    selectedPolarity: mapping?.selectedPolarity ?? null,
  });
}

function notEmptyFilter(field: 'ancestry_name') {
  return {
    name: field,
    operator: 'eq' as const,
    type: 'select' as const,
    value: [] as string[],
    emptiness: 'not_empty' as const,
    isEvidence: true,
  };
}

async function fetchAncestryNotEmptySample(
  scope: EvidenceDistributionScope,
  sampleSize: number,
  auth: FetchAuth,
): Promise<AnnotatedSample & { ok: boolean }> {
  const url = buildEvidenceUrl(scope, sampleSize, [notEmptyFilter('ancestry_name')]);
  if (!url) return { rows: [], annotatedCount: 0, ok: false };

  const response = await fetchEvidencePage(url, auth);
  if (response?.status !== 'OK' && response?.status !== 200) {
    return { rows: [], annotatedCount: 0, ok: false };
  }
  const rows = readRows(response);
  return { rows, annotatedCount: readTotal(response), ok: true };
}

function categoryFromUnfilteredSample(params: {
  id: CategoryDistribution['id'];
  title: string;
  unfilteredRows: Array<Record<string, unknown>>;
  totalRecords: number;
  sampleWindowSize: number;
}): CategoryDistribution {
  const { id, title, unfilteredRows, totalRecords, sampleWindowSize } = params;
  const legacy = computeEvidenceDistributionMetrics(unfilteredRows);
  const fromLegacy =
    id === 'ancestry'
      ? legacy.ancestry
      : id === 'disease_model'
        ? legacy.diseaseModel
        : id === 'sex'
          ? legacy.sex
          : id === 'chemical'
            ? legacy.chemical
            : legacy.associationType;

  const sampledAnnotated = fromLegacy.totalAnnotated;
  const scaledUnspecified =
    unfilteredRows.length > 0
      ? Math.round((fromLegacy.totalUnspecified / unfilteredRows.length) * totalRecords)
      : Math.max(0, totalRecords - sampledAnnotated);

  return {
    ...fromLegacy,
    totalRecords,
    totalAnnotated: Math.max(0, totalRecords - scaledUnspecified),
    totalUnspecified: scaledUnspecified,
    isComplete: unfilteredRows.length >= totalRecords,
    sampledAnnotated,
    sampleWindowSize,
  };
}

function associationTypeFromSample(params: {
  unfilteredRows: Array<Record<string, unknown>>;
  totalRecords: number;
  sampleWindowSize: number;
}): CategoryDistribution {
  const base = categoryFromUnfilteredSample({
    id: 'association_type',
    title: 'Association type',
    unfilteredRows: params.unfilteredRows,
    totalRecords: params.totalRecords,
    sampleWindowSize: params.sampleWindowSize,
  });

  return {
    ...base,
    totalAnnotated: params.totalRecords,
    totalUnspecified: 0,
    // Keep sample completeness based on fetched window, not emptiness.
    isComplete: params.unfilteredRows.length >= params.totalRecords,
  };
}

export async function fetchEvidenceDistributionMetrics(params: {
  scope: EvidenceDistributionScope;
  auth: FetchAuth;
  sampleSize?: number;
}): Promise<EvidenceDistributionMetrics> {
  const sampleSize = params.sampleSize ?? EVIDENCE_SAMPLE_SIZE;
  const { scope, auth } = params;
  const cacheKey = scopeCacheKey(scope);

  const cached = metricsCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.metrics;
  }

  const inflight = inflightByKey.get(cacheKey);
  if (inflight) return inflight;

  const promise = (async () => {
    const baseUrl = buildEvidenceUrl(scope, sampleSize, []);
    if (!baseUrl) {
      return metricsFromAnnotatedFieldSamples({
        totalRecords: 0,
        ancestry: { rows: [], annotatedCount: 0 },
        diseaseModel: { rows: [], annotatedCount: 0 },
        sex: { rows: [], annotatedCount: 0 },
        associationType: { rows: [], annotatedCount: 0 },
        chemical: { rows: [], annotatedCount: 0 },
      });
    }

    const [baseResponse, ancestrySample] = await Promise.all([
      fetchEvidencePage(baseUrl, auth),
      fetchAncestryNotEmptySample(scope, sampleSize, auth),
    ]);

    assertOk(baseResponse, 'Evidence profile');
    const unfilteredRows = readRows(baseResponse);
    const totalRecords = Math.max(readTotal(baseResponse), unfilteredRows.length);

    if (totalRecords === 0 && unfilteredRows.length === 0) {
      return metricsFromAnnotatedFieldSamples({
        totalRecords: 0,
        ancestry: { rows: [], annotatedCount: 0 },
        diseaseModel: { rows: [], annotatedCount: 0 },
        sex: { rows: [], annotatedCount: 0 },
        associationType: { rows: [], annotatedCount: 0 },
        chemical: { rows: [], annotatedCount: 0 },
      });
    }

    const metrics: EvidenceDistributionMetrics = {
      totalRecords,
      ancestry: ancestrySample.ok
        ? buildCategoryFromAnnotatedSample({
            id: 'ancestry',
            title: 'Ancestry',
            rows: ancestrySample.rows,
            totalRecords,
            annotatedCount: ancestrySample.annotatedCount,
            sampleWindowSize: sampleSize,
          })
        : categoryFromUnfilteredSample({
            id: 'ancestry',
            title: 'Ancestry',
            unfilteredRows,
            totalRecords,
            sampleWindowSize: sampleSize,
          }),
      associationType: associationTypeFromSample({
        unfilteredRows,
        totalRecords,
        sampleWindowSize: sampleSize,
      }),
      diseaseModel: categoryFromUnfilteredSample({
        id: 'disease_model',
        title: 'Disease model',
        unfilteredRows,
        totalRecords,
        sampleWindowSize: sampleSize,
      }),
      sex: categoryFromUnfilteredSample({
        id: 'sex',
        title: 'Sex / Gender',
        unfilteredRows,
        totalRecords,
        sampleWindowSize: sampleSize,
      }),
      chemical: categoryFromUnfilteredSample({
        id: 'chemical',
        title: 'Chemical',
        unfilteredRows,
        totalRecords,
        sampleWindowSize: sampleSize,
      }),
    };

    metricsCache.set(cacheKey, { metrics, expiresAt: Date.now() + CACHE_TTL_MS });
    return metrics;
  })();

  inflightByKey.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    inflightByKey.delete(cacheKey);
  }
}

export function clearEvidenceDistributionCache(): void {
  metricsCache.clear();
  inflightByKey.clear();
}

export function describeEvidenceCoverage(metrics: EvidenceDistributionMetrics | null | undefined): {
  allComplete: boolean;
  anySampled: boolean;
  summary: string | null;
} {
  if (!metrics || metrics.totalRecords === 0) {
    return { allComplete: true, anySampled: false, summary: null };
  }

  const exactEmptyFields = [metrics.ancestry];
  const sampledFields = [metrics.diseaseModel, metrics.sex, metrics.associationType, metrics.chemical];
  const allExactComplete = exactEmptyFields.every(c => c.isComplete);
  const allSampleComplete = sampledFields.every(c => c.isComplete);

  if (allExactComplete && allSampleComplete) {
    return {
      allComplete: true,
      anySampled: false,
      summary: `Full profile of ${metrics.totalRecords.toLocaleString()} evidence records`,
    };
  }

  return {
    allComplete: false,
    anySampled: true,
    summary: `Ancestry: exact empties. Disease model, sex, association type & chemical: from sample of ${Math.min(
      EVIDENCE_SAMPLE_SIZE,
      metrics.totalRecords,
    ).toLocaleString()} / ${metrics.totalRecords.toLocaleString()}`,
  };
}
